import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ImageUpload } from "@/components/ImageUpload";
import { toast } from "sonner";

const groups = [
  {
    title: "Business Info",
    fields: [
      { k: "business_name", l: "Business Name" },
      { k: "tagline", l: "Tagline" },
      { k: "logo", l: "Logo", upload: true },
    ],
  },
  {
    title: "Contact",
    fields: [
      { k: "phone", l: "Phone" },
      { k: "whatsapp", l: "WhatsApp Number" },
      { k: "email", l: "Email" },
      { k: "instagram", l: "Instagram URL" },
      { k: "address", l: "Address", area: true },
      { k: "timings", l: "Business Timings" },
      { k: "delivery_info", l: "Delivery Information", area: true },
    ],
  },
  {
    title: "Payment",
    fields: [
      { k: "upi_id", l: "UPI ID" },
      { k: "delivery_charge", l: "Delivery Charge (₹)" },
      { k: "payment_instructions", l: "Payment Instructions", area: true },
      { k: "qr_code", l: "QR Code Image", upload: true },
    ],
  },
];

export default function AdminSettings() {
  const { reload } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/settings/qr")]).then(([s, qr]) =>
      setForm({ ...s.data, qr_code: qr.data.qr_code || "" })
    );
  }, []);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      await reload();
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-heading text-3xl text-brand-dark">Settings</h1>
      <p className="text-brand-muted">These values power your customer website. Replace development placeholders here.</p>

      <div className="mt-6 space-y-6">
        {groups.map((g) => (
          <div key={g.title} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="font-heading text-xl text-brand-dark">{g.title}</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {g.fields.map((f) => (
                <div key={f.k} className={f.area || f.upload ? "sm:col-span-2" : ""}>
                  <label className="mb-1 block text-sm text-gray-600">{f.l}</label>
                  {f.upload ? (
                    <>
                      <input data-testid={`set-${f.k}`} placeholder="Paste image URL or upload below" value={form[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} className="mb-2 w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold" />
                      <ImageUpload testId={`set-${f.k}-upload`} value={form[f.k]} onChange={(v) => set(f.k, v)} label={`Upload ${f.l}`} />
                    </>
                  ) : f.area ? (
                    <textarea data-testid={`set-${f.k}`} value={form[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} rows={2} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold" />
                  ) : (
                    <input data-testid={`set-${f.k}`} value={form[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold" />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button data-testid="save-settings-btn" onClick={save} disabled={saving} className="mt-6 rounded-full bg-brand px-8 py-3 font-medium text-white hover:bg-brand-hover disabled:opacity-60">
        {saving ? "Saving..." : "Save Settings"}
      </button>
    </div>
  );
}
